import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bulkUpdateExtractions,
  getExtractions,
  getExtractionsSummary,
  patchExtraction,
  type ExtractionFilters,
} from "../api/extractions";
import type {
  Extraction,
  ExtractionBulkUpdateRequest,
  ExtractionPatch,
  ExtractionSummaryRow,
} from "../types";

export function useExtractions(
  projectId: string | undefined,
  filters?: ExtractionFilters,
  enabled = true,
) {
  return useQuery<Extraction[]>({
    queryKey: ["extractions", projectId, filters?.label ?? null, filters?.status ?? null],
    queryFn: () => getExtractions(projectId!, filters),
    enabled: !!projectId && enabled,
    staleTime: 10_000,
  });
}

export function useExtractionsSummary(projectId: string | undefined) {
  return useQuery<ExtractionSummaryRow[]>({
    queryKey: ["extractions-summary", projectId],
    queryFn: () => getExtractionsSummary(projectId!),
    enabled: !!projectId,
    staleTime: 10_000,
  });
}

function invalidateReviewQueries(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  qc.invalidateQueries({ queryKey: ["extractions", projectId] });
  qc.invalidateQueries({ queryKey: ["extractions-summary", projectId] });
  qc.invalidateQueries({ queryKey: ["monograph", projectId] });
  qc.invalidateQueries({ queryKey: ["project", projectId] });
}

export function usePatchExtraction(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ExtractionPatch }) =>
      patchExtraction(id, body),
    onSuccess: () => invalidateReviewQueries(qc, projectId),
  });
}

export function useBulkUpdateExtractions(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExtractionBulkUpdateRequest) =>
      bulkUpdateExtractions(projectId, body),
    onSuccess: () => invalidateReviewQueries(qc, projectId),
  });
}
